// @ts-strict-ignore
import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { bindingsDefinition, injected, prop } from '@/hybrid/core/bindings.util';
import { useInjectedBindings } from '@/hybrid/core/hooks/useInjectedBindings.hook';
import { useTranslation } from 'react-i18next';
import { SelectAssetModal } from '@/hybrid/core/SelectAssetModal.molecule';
import { TextButton } from '@/hybrid/core/TextButton.atom';
import { errorToast } from '@/hybrid/utilities/toast.utilities';
import { SEARCH_TYPES } from '@/main/app.constants';
import { DISPLAYS_CATEGORY } from '@/hybrid/displays/displays.constants';
import { computeMoveDisplayAsset } from '@/hybrid/displays/displays.utilities';
import { moveDisplay } from '@/hybrid/displays/displays.actions';
import { doTrack } from '@/track/track.service';
import { sqModalSearchStore } from '@/core/core.stores';
import { exploreAssetSearchActions } from '@/search/search.actions';

const moveDisplayModalBindings = bindingsDefinition({
  display: prop<{ id: string; name: string; ancestors: { id: string }[] }>(),
  onClose: prop<() => void>(),
  $state: injected<ng.ui.IStateService>(),
});

export const MoveDisplayModal: SeeqComponent<typeof moveDisplayModalBindings> = (props) => {
  const { $state } = useInjectedBindings(moveDisplayModalBindings);

  const EXCLUDE_GLOBALLY_SCOPED = true;
  const { display, onClose } = props;
  const { t } = useTranslation();
  const [assetId, setAssetId] = useState();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initialAssetId = computeMoveDisplayAsset(display);
    exploreAssetSearchActions(
      'modal',
      initialAssetId,
      SEARCH_TYPES,
      false,
      $state.params.workbookId,
      EXCLUDE_GLOBALLY_SCOPED,
    )
      .then(() => {
        setIsInitialized(true);
      })
      .catch((error) => errorToast({ httpResponseOrError: error }));
  }, []);

  const isValidAsset = (item): Promise<boolean> => {
    const isScopedToWorkbook = item.scopedTo === $state.params.workbookId;
    const isWorkbookScopedSearch = sqModalSearchStore.mode === 'search';
    const isNotCurrentParentAsset = item.id !== display?.ancestors?.slice(-1)?.[0]?.id;
    const isValid = (isScopedToWorkbook || isWorkbookScopedSearch) && isNotCurrentParentAsset;
    return Promise.resolve(isValid);
  };

  const updateDisplayParent = (parentId) => {
    onClose();
    return moveDisplay(display.id, parentId);
  };

  const cancelMoveDisplay = () => {
    doTrack(DISPLAYS_CATEGORY, 'move', 'cancel');
    onClose();
  };

  const footer = (
    <div className="flexColumnContainer flexJustifyCenter p10">
      <TextButton
        onClick={cancelMoveDisplay}
        label="DISPLAYS.MODAL.MOVE.CANCEL"
        extraClassNames="mr70"
        testId="moveDisplayCancelButton"
      />
      <TextButton
        onClick={() => {
          updateDisplayParent(assetId);
        }}
        disabled={!assetId}
        label="DISPLAYS.MODAL.MOVE.MOVE"
        variant="theme"
        testId="moveDisplayMoveButton"
      />
    </div>
  );

  const title = <Modal.Title data-testid="moveDisplayTitle">{t('DISPLAYS.MODAL.MOVE.HEADER')}</Modal.Title>;

  const body = (
    <div className="alert alert-info" data-testid="moveDisplayHelpText">
      {t('DISPLAYS.MODAL.MOVE.HELP_TEXT', { DISPLAY_NAME: display?.name })}
    </div>
  );

  return isInitialized ? (
    <SelectAssetModal
      onClose={cancelMoveDisplay}
      iconPartialTooltipKey="DISPLAYS.MODAL.MOVE.TOOLTIPS"
      onSelect={(item) => {
        setAssetId(item.id);
      }}
      validateAsset={isValidAsset}
      assetId={assetId}
      scopeIds={[$state.params.workbookId]}
      excludeGloballyScoped={true}
      body={body}
      header={title}
      footer={footer}
      testId="moveDisplayModal"
    />
  ) : null;
};
